import { MetricsTimePeriod } from '@/@admin/types/entities';

/**
 * Get time period display text
 */
export const getTimePeriodText = (timePeriod: MetricsTimePeriod): string => {
    const periodMap: Record<MetricsTimePeriod, string> = {
        [MetricsTimePeriod.TODAY]: 'today',
        [MetricsTimePeriod.ONE_WEEK]: 'the past week',
        [MetricsTimePeriod.TWO_WEEKS]: 'the past 2 weeks',
        [MetricsTimePeriod.ONE_MONTH]: 'the past month',
        [MetricsTimePeriod.TWO_MONTHS]: 'the past 2 months',
        [MetricsTimePeriod.SIX_MONTHS]: 'the past 6 months',
        [MetricsTimePeriod.ONE_YEAR]: 'the past year',
        [MetricsTimePeriod.ALL]: 'all time',
    };
    return periodMap[timePeriod] || 'the selected period';
};

/**
 * Get dynamic descriptions for metrics based on time period
 */
export const getMetricDescriptions = (timePeriod: MetricsTimePeriod, studioAIName: string) => {
    const periodText = getTimePeriodText(timePeriod);
    const percentage = 67;

    return {
        engagement: `Average number of ${studioAIName} queries or sessions per user per week in ${periodText}`,
        validatedAccuracy: `Percentage of ${studioAIName} outputs marked as "usable" by Research and CS review teams`,
        deliverableImpact: `${percentage}% of projects included ${studioAIName}-generated insights in final deliverables, demonstrating tangible value creation.`,
        userSentiment: (ratio: number) =>
            ratio > 0
                ? `${ratio.toFixed(0)}:1 positive-to-negative feedback ratio from user responses`
                : 'No feedback data available for this period.',
        activeUserRate: `Percentage of invited users who actively used ${studioAIName} tools in ${periodText}`,
        studioAIUsers: `Users with ${studioAIName} access`,
        activeInPeriod: `Active in ${periodText}`,
        newInPeriod: `Created in ${periodText}`,
        avgConversationsPerUser: `Per active user in ${periodText}`,
        avgUserMessagesPerUser: `Per active user in ${periodText}`,
        avgDaysActive: `Average distinct days per user in ${periodText}`,
    };
};

/**
 * Tooltip calculations for all metrics
 */
export const getMetricTooltips = (studioAIName: string) => ({
    // Adoption & Activation
    adoptionRate: `Calculation: (Active ${studioAIName} Users ÷ Total Users with ${studioAIName} Access) × 100. Active users are those who have interacted with ${studioAIName} during the selected time period.`,
    orgActivationRate: `Calculation: (Clients with at least one active user within 2 weeks of onboarding ÷ Total Clients) × 100. Measures how quickly clients start using ${studioAIName} after access is granted.`,
    avgActiveUsersPerOrg: `Calculation: Total Active Users ÷ Number of Clients with at least one active user. Only includes clients that have users who engaged with ${studioAIName}.`,

    // Days Active & Engagement Depth
    avgDaysActive: `Calculation: Average of distinct calendar days each user was active during the selected period. A higher number indicates more consistent usage patterns.`,
    avgSessionsPerUserPerWeek: `Calculation: (New Conversations in Period ÷ Active Users ÷ Weeks in Period). Measures how frequently users engage with ${studioAIName} on a weekly basis.`,
    engagement: `Calculation: Average User Messages per User ÷ Weeks in Period. Represents the intensity of user engagement with ${studioAIName}.`,
    userEngagement: `Users engagement metricsbased on a composite score of days active, conversations, and messages. Provides individual-level engagement visibility.`,
    dayOfWeekActivity: `Breakdown of activity by day of week. Helps identify usage patterns (weekday vs weekend) and peak engagement days.`,

    // User Activity
    totalActiveUsers: `Total number of users with active status who have ${studioAIName} access enabled AND are counted in metrics (countInMetrics = true).`,
    usersWithStudioAI: `Number of users who have ${studioAIName} access enabled and are counted in metrics.`,
    activeStudioAIUsers: `Number of users who have used ${studioAIName} (created at least one conversation with a valid thread) during the selected time period.`,

    // Conversations
    totalConversations: `Total number of non-deleted conversations with valid threads (thread_id IS NOT NULL) ever created.`,
    newConversations: `Number of conversations created during the selected time period.`,
    avgConversationsPerUser: `Calculation: New Conversations ÷ Active Users. Average number of conversations started per active user during the period.`,
    deletedConversations: `Number of conversations that have been soft-deleted by users.`,
    avgMessagesPerNewConversation: `Calculation: Average of message counts from conversations created in the selected time period. Only includes conversations with valid threads.`,

    // Messages
    totalMessages: `Total count of all messages (user + assistant) from conversations active in the selected period.`,
    avgMessagesPerConversation: `Calculation: Total Messages ÷ Total Active Conversations in period. Average back-and-forth per conversation.`,
    avgUserMessagesPerUser: `Calculation: Total User Messages ÷ Active Users. Average number of messages sent by users.`,

    // Feedback
    totalFeedback: `Total number of feedback submissions (thumbs up/down) during the selected period.`,
    positiveFeedback: `Number of positive (thumbs up) feedback submissions.`,
    negativeFeedback: `Number of negative (thumbs down) feedback submissions.`,
    noFeedback: `Number of conversations that have no feedback submissions.`,
    positiveFeedbackRate: `Calculation: (Positive Feedback ÷ Total Feedback) × 100. Percentage of feedback that is positive.`,
    negativeFeedbackRate: `Calculation: (Negative Feedback ÷ Total Feedback) × 100. Percentage of feedback that is negative.`,
    noFeedbackRate: `Calculation: (Conversations without Feedback ÷ Total Conversations) × 100.`,
    responseRate: `Calculation: (Feedback with Admin Response ÷ Total Feedback) × 100. Percentage of feedback that has received an admin response.`,
    negativeFeedbackNeedingResponse: `Number of negative feedback items that have not yet received an admin response.`,

    // Sentiment
    userSentiment: `Visual breakdown of feedback sentiment: Positive (thumbs up), Negative (thumbs down), and No Feedback (conversations without any feedback).`,
});

/** Format a YYYY-MM-DD (UTC) date string to a short display label (e.g. "Jan 15") */
export function formatTrendDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
